// @tg-seq: 10979

/**
 * Organization Time & Workforce Dashboard
 *
 * Manager-facing, organization-level view (replaces the retired per-user
 * "6-panel" HF mockup). Real data only — no mock state.
 *
 * Sections:
 * - Pending PTO Approvals (org-scoped via Organization_UUID, Approve/Reject wired)
 * - Time Study Billable Summary (workforce-wide monthly aggregate; the
 *   underlying stored procedure does not currently filter by organization,
 *   so this reflects all users on the platform, not just this org — labeled
 *   accordingly rather than presented as strictly org-scoped)
 *
 * Individual/"my" workflows live in their own tabs:
 * scr_tgc_10923 (My Time Study), scr_tgc_10924 (Time Study History),
 * scr_tgc_10922 (Time Punch), scr_tgc_10921 (Time Card History),
 * scr_tgc_10920 (Time Analytics & PTO).
 */

import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import GroupsIcon from '@mui/icons-material/Groups';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import {
    Box,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { apiRequestBySeq } from '../../utils/apiService';
import { getTsxFileName } from '../../utils/componentIdentity';
import { useAuth } from '../../utils/useAuth';

interface PendingPTORequest {
    ptorequest_uuid: string;
    pfuser_uuid: string;
    name_first: string;
    name_last: string;
    pto_type_name: string;
    applyto_name: string;
    start_date: string;
    end_date: string;
    hours: number | null;
    duration_days: number;
    notes: string | null;
}

interface BillableSummaryRow {
    period_month: string;
    billable_hours: number;
    nonbillable_hours: number;
    total_hours: number;
    billable_study_count: number;
    total_study_count: number;
}

const Self: React.FC = () => {
    const { pfUser, activeOrganizationUUID } = useAuth();
    const normalizedOrgUUID =
        activeOrganizationUUID && activeOrganizationUUID !== 'ALL' ? activeOrganizationUUID : null;

    const [loading, setLoading] = useState<boolean>(false);
    const [pendingPTO, setPendingPTO] = useState<PendingPTORequest[]>([]);
    const [billableSummary, setBillableSummary] = useState<BillableSummaryRow[]>([]);
    const [actioningUuid, setActioningUuid] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const loadDashboard = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [ptoResponse, summaryResponse]: any[] = await Promise.all([
                apiRequestBySeq(10920, 2, {
                    manage: 5,
                    organization_uuid: normalizedOrgUUID,
                    ptoapprovalstatus_id: 1, // Pending
                }),
                apiRequestBySeq(10924, 1, { manage: 10 }),
            ]);
            setPendingPTO(ptoResponse?.data || []);
            setBillableSummary(summaryResponse?.data || []);
        } catch (err) {
            console.error('[Org Dashboard] Failed to load dashboard data:', err);
            setError('Failed to load organization dashboard data.');
        } finally {
            setLoading(false);
        }
    }, [normalizedOrgUUID]);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const handleReviewPTO = async (ptorequest_uuid: string, approve: boolean) => {
        if (!pfUser?.user_uuid) return;
        setActioningUuid(ptorequest_uuid);
        try {
            await apiRequestBySeq(10920, 2, {
                manage: approve ? 6 : 7,
                ptorequest_uuid,
                reviewed_by_pfuser_uuid: pfUser.user_uuid,
            });
            await loadDashboard();
        } catch (err) {
            console.error('[Org Dashboard] Failed to review PTO request:', err);
            setError('Failed to update PTO request.');
        } finally {
            setActioningUuid(null);
        }
    };

    const currentMonth = billableSummary[0];

    if (!pfUser?.user_uuid) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography
                    sx={{
                        color: 'text.secondary',
                    }}
                >
                    Loading user context...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
            <Paper
                sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                <GroupsIcon />
                <Typography variant="h6">Organization Time &amp; Workforce Dashboard</Typography>
            </Paper>

            {error && (
                <Typography
                    variant="body2"
                    sx={{
                        color: 'error.main',
                    }}
                >
                    {error}
                </Typography>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                <>
                    {/* ═══ KPI CARDS (workforce-wide, current month) ═══ */}
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                            }}
                        >
                            <Paper sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="h4" color="primary">
                                    {currentMonth?.billable_hours ?? 0}
                                </Typography>
                                <Typography variant="caption">Billable Hours (Month)</Typography>
                            </Paper>
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                            }}
                        >
                            <Paper sx={{ p: 2, textAlign: 'center' }}>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: 'warning.main',
                                    }}
                                >
                                    {currentMonth?.nonbillable_hours ?? 0}
                                </Typography>
                                <Typography variant="caption">
                                    Non-Billable Hours (Month)
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                            }}
                        >
                            <Paper sx={{ p: 2, textAlign: 'center' }}>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: 'info.main',
                                    }}
                                >
                                    {currentMonth?.total_study_count ?? 0}
                                </Typography>
                                <Typography variant="caption">Studies This Month</Typography>
                            </Paper>
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                            }}
                        >
                            <Paper sx={{ p: 2, textAlign: 'center' }}>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: 'error.main',
                                    }}
                                >
                                    {pendingPTO.length}
                                </Typography>
                                <Typography variant="caption">Pending PTO Requests</Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* ═══ PENDING PTO APPROVALS (org-scoped) ═══ */}
                    <Paper sx={{ p: 2 }}>
                        <Typography
                            variant="subtitle1"
                            sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            <BeachAccessIcon color="primary" fontSize="small" />
                            Pending PTO Approvals
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Dates</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pendingPTO.map((req) => (
                                        <TableRow key={req.ptorequest_uuid}>
                                            <TableCell>
                                                {req.name_first} {req.name_last}
                                            </TableCell>
                                            <TableCell>
                                                <Chip size="small" label={req.pto_type_name} />
                                            </TableCell>
                                            <TableCell>
                                                {new Date(req.start_date).toLocaleDateString()} -{' '}
                                                {new Date(req.end_date).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell>
                                                {req.duration_days} day
                                                {req.duration_days === 1 ? '' : 's'}
                                            </TableCell>
                                            <TableCell>{req.notes || '—'}</TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Approve">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="success"
                                                            disabled={
                                                                actioningUuid ===
                                                                req.ptorequest_uuid
                                                            }
                                                            onClick={() =>
                                                                handleReviewPTO(
                                                                    req.ptorequest_uuid,
                                                                    true,
                                                                )
                                                            }
                                                        >
                                                            <CheckCircleIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Reject">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            disabled={
                                                                actioningUuid ===
                                                                req.ptorequest_uuid
                                                            }
                                                            onClick={() =>
                                                                handleReviewPTO(
                                                                    req.ptorequest_uuid,
                                                                    false,
                                                                )
                                                            }
                                                        >
                                                            <CancelIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {pendingPTO.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    No pending PTO requests.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    {/* ═══ BILLABLE SUMMARY BY MONTH (workforce-wide) ═══ */}
                    <Paper sx={{ p: 2 }}>
                        <Typography
                            variant="subtitle1"
                            sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            <ShowChartIcon color="primary" fontSize="small" />
                            Time Study Billable Summary (All Users)
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Month</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">
                                            Billable Hours
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">
                                            Non-Billable Hours
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">
                                            Total Hours
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">
                                            Studies
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {billableSummary.map((row) => (
                                        <TableRow key={row.period_month}>
                                            <TableCell>
                                                {new Date(row.period_month).toLocaleDateString(
                                                    undefined,
                                                    { year: 'numeric', month: 'long' },
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {row.billable_hours}
                                            </TableCell>
                                            <TableCell align="right">
                                                {row.nonbillable_hours}
                                            </TableCell>
                                            <TableCell align="right">{row.total_hours}</TableCell>
                                            <TableCell align="right">
                                                {row.billable_study_count}/{row.total_study_count}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {billableSummary.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    No completed time studies yet.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </>
            )}
        </Box>
    );
};
Self.displayName = getTsxFileName(import.meta.url);
export default Self;
